import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Heart, FileText, Users, Scale, Map, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ContactForm from '../components/ContactForm';
import SEO from '../components/SEO';

const Estate = () => {
  const services = [
    {
      icon: FileText,
      title: 'Wills & Trusts',
      description: 'Comprehensive estate documents including wills, revocable and irrevocable trusts tailored to your needs.',
    },
    {
      icon: Shield,
      title: 'Asset Protection',
      description: 'Strategic planning to protect your assets from creditors, lawsuits, and estate taxes.',
    },
    {
      icon: Heart,
      title: 'Legacy Planning',
      description: 'Preserve your values and ensure your wealth benefits future generations as intended.',
    },
    {
      icon: Users,
      title: 'Family Succession Planning',
      description: 'Smooth wealth transfer strategies for family businesses and multi-generational wealth.',
    },
    {
      icon: Scale,
      title: 'Estate Tax Minimization',
      description: 'Advanced strategies to reduce estate and gift tax liability through proper planning.',
    },
    {
      icon: Map,
      title: 'Nationwide Availability',
      description: 'Estate services available in all 50 states through our team of licensed attorneys.',
    },
  ];

  const processSteps = [
    {
      title: 'Estate Analysis',
      description: 'Comprehensive review of your assets, family situation, and estate planning goals.',
    },
    {
      title: 'Strategy Development',
      description: 'Custom estate plan design by our in-house attorneys licensed in all 50 states.',
    },
    {
      title: 'Document Preparation',
      description: 'Professional drafting of all necessary legal documents including wills, trusts, and powers of attorney.',
    },
    {
      title: 'Implementation & Review',
      description: 'Execute your estate plan and establish ongoing review to ensure it remains current.',
    },
  ];

  const commonDocuments = [
    'Last Will and Testament',
    'Revocable Living Trusts',
    'Irrevocable Life Insurance Trusts',
    'Healthcare Directives',
    'Powers of Attorney',
    'Charitable Trusts',
    'Generation-Skipping Trusts',
    'Family Limited Partnerships',
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Estate Planning Services",
    "provider": {
      "@type": "FinancialService",
      "name": "Paracca Financial Group"
    },
    "areaServed": "nationwide in all 50 states",
    "description": "Professional estate planning and attorney services for individuals nationwide. Nationwide coverage in all 50 states."
  };

  return (
    <>
      <SEO 
        title="Estate Services | Attorneys in All 50 States | Paracca Financial Group"
        description="Expert estate planning, trusts, wills, and attorneys nationwide serving all 50 states. Complete estate services with nationwide coverage in all 50 states."
        keywords="estate services sarasota fl, estate attorney sarasota, wills and trusts florida, estate planning venice, asset protection sarasota"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative h-[400px] md:h-[500px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1562564055-71e051d33c19')`,
          }}
        >
          <div className="absolute inset-0 bg-[#0a69b2] opacity-70"></div>
        </div>
        
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-3xl">
            <h1 className="font-bellefair text-4xl md:text-6xl  text-white mb-6 uppercase">
              Estate Services
            </h1>
            <p className="font-montserrat text-lg md:text-xl text-gray-100 leading-relaxed">
              Complete estate planning, trusts, wills, and legacy protection with attorneys available in all 50 states
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-6 uppercase">
              Comprehensive Estate Services
            </h2>
            <p className="font-montserrat text-lg text-gray-600 leading-relaxed mb-8">
              At Paracca Financial Group, our in-house estate attorneys provide comprehensive services including estate planning, trusts, wills, and asset protection strategies to clients nationwide. Unlike other firms that refer estate work to outside counsel, we offer fully integrated estate services as part of our holistic wealth management approach—serving clients in all 50 states.
            </p>
            <p className="font-montserrat text-lg text-gray-600 leading-relaxed">
              Serving individuals and families throughout the Nationwide area, all 50 states, and nationwide—our estate planning services extend to all 50 states with attorneys licensed across the country to serve you wherever you're located.
            </p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              Our Estate Planning Services
            </h2>
            <p className="font-montserrat text-lg text-gray-600">
              Comprehensive legal solutions to protect your wealth and legacy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={service.title} className="hover:shadow-xl transition-shadow duration-300 border-2">
                  <CardContent className="p-6">
                    <div className="bg-[#0a69b2] w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <h3 className="font-bellefair text-xl  text-[#0a69b2] mb-3 uppercase">
                      {service.title}
                    </h3>
                    <p className="font-montserrat text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              Our Estate Planning Process
            </h2>
            <p className="font-montserrat text-lg text-gray-600 max-w-3xl mx-auto">
              A systematic approach to protecting your assets and securing your legacy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, stepIndex) => (
              <div key={step.title} className="relative">
                <div className="bg-[#0a69b2] text-white w-12 h-12 rounded-full flex items-center justify-center font-bellefair text-xl  mb-4">
                  {stepIndex + 1}
                </div>
                <h3 className="font-bellefair text-xl  text-[#0a69b2] mb-3 uppercase">
                  {step.title}
                </h3>
                <p className="font-montserrat text-gray-600 leading-relaxed">
                  {step.description}
                </p>
                {stepIndex < processSteps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-6 -right-4 text-gray-300" size={24} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11"
                alt="Estate Planning Attorney Nationwide Service - Trust and Will Services Nationwide"
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#0a69b2] text-white p-8 rounded-lg shadow-xl max-w-xs">
                <p className="font-bellefair text-4xl  mb-2">All 50 States</p>
                <p className="font-montserrat text-sm">Our attorneys are licensed to serve you nationwide</p>
              </div>
            </div>
            <div>
              <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-6 uppercase">
                Comprehensive Estate Documents
              </h2>
              <p className="font-montserrat text-lg text-gray-600 mb-8 leading-relaxed">
                Our in-house attorneys prepare all the necessary legal documents to protect your estate and ensure your wishes are carried out. Every document is tailored to your specific needs and goals.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {commonDocuments.map((doc) => (
                  <div key={doc} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0a69b2] flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="font-montserrat text-gray-700">{doc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-6 uppercase">
            Integrated with Your Financial Strategy
          </h2>
          <p className="font-montserrat text-lg text-gray-600 leading-relaxed mb-8">
            Estate planning at Paracca Financial Group isn't done in isolation. Our attorneys work hand-in-hand with your asset management and tax planning teams to ensure your estate plan is fully coordinated with your overall wealth strategy. This integration maximizes tax efficiency, protects assets, and ensures your legacy goals are achieved.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="bg-gray-50 p-6 rounded-lg">
              <Shield className="text-[#0a69b2] mx-auto mb-4" size={40} />
              <h3 className="font-bellefair text-lg  text-[#0a69b2] mb-2 uppercase">Asset Protection</h3>
              <p className="font-montserrat text-sm text-gray-600">Coordinated with investment strategy</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <FileText className="text-[#0a69b2] mx-auto mb-4" size={40} />
              <h3 className="font-bellefair text-lg  text-[#0a69b2] mb-2 uppercase">Tax Efficiency</h3>
              <p className="font-montserrat text-sm text-gray-600">Aligned with CPA tax planning</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <Heart className="text-[#0a69b2] mx-auto mb-4" size={40} />
              <h3 className="font-bellefair text-lg  text-[#0a69b2] mb-2 uppercase">Legacy Goals</h3>
              <p className="font-montserrat text-sm text-gray-600">Reflects your values & vision</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0a69b2] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bellefair text-3xl md:text-4xl  mb-6 uppercase">
            Protect Your Legacy Today
          </h2>
          <p className="font-montserrat text-lg mb-8 leading-relaxed text-gray-100">
            Schedule a consultation with our estate planning attorneys
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-[#0a69b2] hover:bg-gray-100 font-montserrat font-semibold px-8 py-6 transition-all duration-300"
              asChild
            >
              <a href="tel:941-545-9057">
                Call 941-545-9057
              </a>
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0a69b2] font-montserrat font-semibold px-8 py-6"
              asChild
            >
              <Link to="/">
                Learn More
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-bellefair text-3xl md:text-4xl  text-[#0a69b2] mb-4 uppercase">
              Request an Estate Planning Consultation
            </h2>
            <p className="font-montserrat text-lg text-gray-600">
              Let our attorneys help you secure your legacy
            </p>
          </div>
          <ContactForm />
        </div>
      </section>
    </>
  );
};

export default Estate;
